import {find, isEmpty} from 'lodash';

import {ENABLE_TESTING_OPTIONS} from '/config/testingOptions.config';
import {
  BAD_REQUEST,
  FAILED_TO_PROCESS_REQUEST_EXCEPTION,
  NO_NETWORK_EXCEPTION,
  NOT_AUTHORIZED_EXCEPTION,
  SERVER_IS_DOWN_EXCEPTION,
  UNKNOWN_EXCEPTION,
} from '/enum/errors.enum';
import {
  MY_TEAMS,
  REPORT_ALL_CANDIDATES,
  REPORT_ALL_COACHES_IN_ORGANIZATION,
  REPORT_ALL_FRONTLINE_CLIENTS,
  REPORT_ALL_FRONTLINE_COACHES,
  REPORT_ALL_FRONTLINE_ORDERS,
  REPORT_ALL_GEN0_CLIENTS,
  REPORT_ALL_GEN0_COACHES,
  REPORT_ALL_GEN0_ORDERS,
  REPORT_ALL_ORDERS,
  REPORT_ALL_ORGANIZATION_CLIENTS,
  REPORT_ALL_ORGANIZATION_ORDERS,
  REPORT_ALL_REFERRALS,
  REPORT_BUSINESS_BUILDER_COACHES,
  // REPORT_CLIENT_TO_CONTACT_EXPIRING_REWARDS,
  REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS,
  REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE,
  REPORT_CLIENT_TO_CONTACT_NEXT_TASK,
  REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER,
  REPORT_CLIENT_TO_CONTACT_NO_ORDER,
  REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER,
  REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION,
  REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE,
  REPORT_CLOSE_TO_EXECUTIVE_DIRECTOR_RANK,
  REPORT_CLOSE_TO_FIBC_RANK,
  REPORT_CLOSE_TO_SENIOR_COACH_RANK,
  REPORT_COACHES_ABOVE_RANK,
  REPORT_COACHES_AT_RANK,
  REPORT_COACHES_BELOW_RANK,
  REPORT_EARNED_ACCELERATOR_BONUS,
  REPORT_EARNED_CAB,
  REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_EARNED_QSB,
  REPORT_ELIGIBLE_ACCELERATOR_BONUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
  REPORT_ELIGIBLE_FOR_CAB,
  REPORT_ELIGIBLE_FOR_INCENTIVE_TRIP,
  REPORT_ELIGIBLE_FOR_QSB,
  REPORT_EXPIRING_COACH_RENEWAL,
  REPORT_EXPIRING_REWARDS,
  REPORT_EXPIRING_WELLNESS,
  REPORT_FRONTLINE_COACHES,
  REPORT_INACTIVE_CLIENTS,
  REPORT_NEW_CLIENTS_WITHOUT_ORDERS,
  REPORT_NEW_COACHES_IN_ORGANIZATION,
  REPORT_NEW_FRONTLINE_CLIENTS,
  REPORT_NEW_FRONTLINE_COACHES,
  REPORT_NEW_FRONTLINE_ORDERS,
  REPORT_NEW_GEN0_CLIENTS,
  REPORT_NEW_GEN0_COACHES,
  REPORT_NEW_GEN0_ORDERS,
  REPORT_NEW_ORGANIZATION_CLIENTS,
  REPORT_NEW_ORGANIZATION_ORDERS,
  REPORT_NOT_CERTIFIED,
  REPORT_PREMIER_CANCELLATIONS,
  REPORT_PROJECTED_PREMIER_VOLUME,
  REPORT_QUALIFIED_FOR_INCENTIVE_TRIP,
  REPORT_RANK_UP,
  REPORT_TEAMS_EXECUTIVE_DIRECTOR,
  REPORT_TEAMS_FIBC,
  REPORT_TEAMS_FIBL,
  REPORT_TEAMS_SENIOR_COACH,
  VIEW_TEAMS,
} from '/enum/records.enum';
import {
  CANDIDATES,
  CLIENT_JOURNEY_ALL_TASKS,
  CLIENT_JOURNEY_TASKS,
  CLIENT_TO_CONTACT,
  DELETE_CANDIDATE_REMINDER,
  DISMISS_CLIENTS_ALERTS,
  FETCH_ABACUS_CONVERSATION_RESP,
  FETCH_AUTH_TOKEN,
  FETCH_BONUS_BANNER,
  FETCH_BONUSES,
  FETCH_BUSINESS_PROFILE,
  FETCH_CANDIDATE,
  FETCH_CANDIDATE_DECLINE_REASONS,
  FETCH_CANDIDATE_DISPOSITION_OPTIONS,
  FETCH_CLIENT,
  FETCH_CLIENT_BODY_MEASUREMENTS,
  FETCH_CLIENT_MEAL_PLAN,
  FETCH_CLIENT_MY_WHY,
  FETCH_CLIENT_ORDER,
  FETCH_CLIENT_ORDERS,
  FETCH_CLIENT_START_DATE,
  FETCH_CLIENT_WEIGHT_GRAPH_DETAILS,
  FETCH_CLIENTS_ALERTS,
  FETCH_CLIENTS_DASHBOARD,
  FETCH_COACH,
  FETCH_COACH_PERFORMANCE,
  FETCH_COACH_RANK_PROGRESSION,
  FETCH_COACH_SHARE_PLAN,
  FETCH_COACH_TEAMS,
  FETCH_EDGE_365_CHALLENGE_TASKS,
  FETCH_HEALTH_CARE_DATA,
  FETCH_HOME_DASHBOARD,
  FETCH_INCENTIVE_TRIP_BONUS_DASHBOARD,
  FETCH_LEADS_POOL,
  FETCH_LOGOUT,
  FETCH_MERGECANDIDATE,
  FETCH_MSWL_HEALTH_ROOT_PROFILE,
  FETCH_MULE_AUTH_TOKEN,
  FETCH_MULE_REVOKE_TOKEN,
  FETCH_MULE_TOKEN,
  FETCH_MWL_HEALTH_ROOT_PROFILE,
  FETCH_REFRESH_TOKEN,
  FETCH_RESET_PASSWORD,
  FETCH_SAME_DAY_LAST_MONTH,
  FETCH_SFMC_CONTACT_KEY,
  FETCH_SOCIAL_MEDIA,
  FETCH_SUPPORTED_LANGUAGES,
  FETCH_TEAMS_DASHBOARD,
  FETCH_TGENV_VOLUME,
  FETCH_VOLUME_PERFORMANCE_REPORT,
  PATCH_BUSINESS_PROFILE,
  PATCH_LEADS_POOL,
  PATCH_SOCIAL_MEDIA,
  POST_CANDIDATE_REMINDER,
  POST_COACH_IMAGE,
  PUT_HEALTH_CARE_DATA,
  PUT_SUPPORTED_LANGUAGES,
  SEARCH_COACH,
  UPDATE_CANDIDATE_REMINDER,
  UPDATE_EDGE_365_CHALLENGE_TASKS,
  WATCHLIST,
} from '/enum/request.enum';
import {getErrorTrackingUtilDefaultInstance} from '/util/error-tracking/error-tracking.util';
import {fetchJSON} from '/util/fetch.util';

import {
  getTestingCurrentPeriodId,
  getTestingDate,
  getTestingLastPeriodId,
  getTestingLatestAcceptedPeriodId,
  getTestingOpenPeriodId,
} from './storagePeriod';

const REQUEST_ROUTE = {
  [FETCH_VOLUME_PERFORMANCE_REPORT]: 'Orders/OrderHistory/Teams',
  [FETCH_AUTH_TOKEN]: 'login',
  [FETCH_REFRESH_TOKEN]: 'refresh-token',
  [FETCH_LOGOUT]: 'logout',
  [FETCH_MULE_TOKEN]: 'token',
  [FETCH_ABACUS_CONVERSATION_RESP]: '/getStreamingConversationResponse',
  [FETCH_RESET_PASSWORD]: 'reset-password',
  [FETCH_CLIENTS_DASHBOARD]: 'Dashboards/Clients',
  [FETCH_TEAMS_DASHBOARD]: 'Dashboards/Teams',
  [FETCH_HOME_DASHBOARD]: 'Dashboards/Home',
  [FETCH_COACH_RANK_PROGRESSION]: 'Coaches/RankProgression',
  [FETCH_COACH_PERFORMANCE]: 'Coaches/Performance',
  [FETCH_COACH_TEAMS]: 'Coaches/Teams',
  [FETCH_CLIENT]: 'Clients/Client',
  [FETCH_CANDIDATE]: 'Candidates',
  [WATCHLIST]: 'Coaches/WatchList',
  [VIEW_TEAMS]: 'Reports/TeamReports/MyTeams/ViewTeams',
  [FETCH_MERGECANDIDATE]: 'Candidates-merge-profile',
  [FETCH_CLIENT_ORDERS]: 'Orders/OrderHistory',
  [FETCH_CLIENT_ORDER]: 'Orders/Order',
  [FETCH_COACH]: 'Coaches/Coach',
  [FETCH_INCENTIVE_TRIP_BONUS_DASHBOARD]: 'Dashboards/IncentiveTripBanner',
  [FETCH_BONUS_BANNER]: 'Dashboards/BonusBanner',
  [CLIENT_TO_CONTACT]: 'Dashboards/ClientToContact',

  //ClientJourney tasks
  [CLIENT_JOURNEY_ALL_TASKS]: 'Clients/MasterPickList',
  [CLIENT_JOURNEY_TASKS]: 'Clients/JourneyTasks',

  //Search
  [SEARCH_COACH]: 'Coaches/GlobalSearch',

  //SameDayLastMonth
  [FETCH_SAME_DAY_LAST_MONTH]: 'Coaches/SameDayLastMonth',

  //Bonuses
  [FETCH_BONUSES]: 'Coaches/CommissionReport',

  //Contributing Coaches Report
  [REPORT_BUSINESS_BUILDER_COACHES]: 'Coaches/BusinessBuilderCoaches',

  // ClientReports
  [REPORT_ALL_FRONTLINE_CLIENTS]: 'Reports/ClientReports/AllFrontlineClients',
  [REPORT_ALL_GEN0_CLIENTS]: 'Reports/ClientReports/AllGenZeroClients',
  [REPORT_ALL_ORGANIZATION_CLIENTS]:
    'Reports/ClientReports/AllOrganizationClients',
  [REPORT_NEW_FRONTLINE_CLIENTS]: 'Reports/ClientReports/NewFrontlineClients',
  [REPORT_NEW_GEN0_CLIENTS]: 'Reports/ClientReports/NewGenZeroClients',
  [REPORT_NEW_ORGANIZATION_CLIENTS]:
    'Reports/ClientReports/NewOrganizationClients',
  [REPORT_ALL_FRONTLINE_ORDERS]: 'Reports/ClientReports/AllFrontlineOrders',
  [REPORT_ALL_ORDERS]: 'Orders/AllOrders',
  [REPORT_ALL_GEN0_ORDERS]: 'Reports/ClientReports/AllGenZeroOrders',
  [REPORT_ALL_ORGANIZATION_ORDERS]:
    'Reports/ClientReports/AllOrganizationOrders',
  [REPORT_NEW_FRONTLINE_ORDERS]: 'Reports/ClientReports/NewFrontlineOrders',
  [REPORT_NEW_GEN0_ORDERS]: 'Reports/ClientReports/NewGenZeroOrders',
  [REPORT_NEW_ORGANIZATION_ORDERS]:
    'Reports/ClientReports/NewOrganizationOrders',
  [REPORT_PROJECTED_PREMIER_VOLUME]:
    'Reports/ClientReports/ProjectedPremierVolume',
  [REPORT_PREMIER_CANCELLATIONS]: 'Reports/ClientReports/PremierCancellations',
  [REPORT_NEW_CLIENTS_WITHOUT_ORDERS]:
    'Reports/ClientReports/NewClientsWithoutOrders',
  [REPORT_INACTIVE_CLIENTS]:
    'Reports/ClientReports/InactiveOrganizationClients',
  // [REPORT_CLIENT_TO_CONTACT_EXPIRING_REWARDS]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS]:
    'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_NO_ORDER]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_NEXT_TASK]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER]: 'Dashboards/ClientToContact',
  [REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE]: 'Dashboards/ClientToContact',
  [REPORT_ALL_CANDIDATES]: 'Candidates',
  [REPORT_ALL_REFERRALS]: 'Candidates/referral',
  [FETCH_CANDIDATE_DECLINE_REASONS]: 'Candidates/ReasonCodes',
  [FETCH_CANDIDATE_DISPOSITION_OPTIONS]: 'Candidates/ReasonCodes',
  // Team Reports Volumes
  [FETCH_TGENV_VOLUME]: 'Reports/TeamReports/TGenVolume/Coach',

  // TeamReports
  [REPORT_RANK_UP]: 'Reports/TeamReports/Promotions',
  [REPORT_ELIGIBLE_FOR_CAB]: 'Reports/TeamReports/EligibleForCAB',
  [REPORT_ELIGIBLE_FOR_QSB]: 'Reports/TeamReports/EligibleForQSB',
  [REPORT_CLOSE_TO_EXECUTIVE_DIRECTOR_RANK]: 'Reports/TeamReports/CloseToED',
  [REPORT_CLOSE_TO_FIBC_RANK]: 'Reports/TeamReports/CloseToFIBC',
  [REPORT_CLOSE_TO_SENIOR_COACH_RANK]: 'Reports/TeamReports/CloseToSC',
  [REPORT_TEAMS_EXECUTIVE_DIRECTOR]: 'Reports/TeamReports/EDTeams',
  [REPORT_TEAMS_FIBC]: 'Reports/TeamReports/FIBCTeams',
  [REPORT_TEAMS_FIBL]: 'Reports/TeamReports/FIBLTeams',
  [REPORT_TEAMS_SENIOR_COACH]: 'Reports/TeamReports/SCTeams',
  [REPORT_EXPIRING_COACH_RENEWAL]: 'Reports/TeamReports/ExpiringRenewals',
  [REPORT_COACHES_AT_RANK]: 'Reports/TeamReports/AtRank',
  [REPORT_COACHES_BELOW_RANK]: 'Reports/TeamReports/BelowRank',
  [REPORT_COACHES_ABOVE_RANK]: 'Reports/TeamReports/AboveRank',
  [REPORT_ALL_FRONTLINE_COACHES]: 'Reports/TeamReports/AllFrontlineCoaches',
  [REPORT_FRONTLINE_COACHES]: 'Reports/TeamReports/AllFrontlineCoaches',
  [REPORT_NEW_FRONTLINE_COACHES]: 'Reports/TeamReports/NewFrontlineCoaches',
  [REPORT_ALL_GEN0_COACHES]: 'Reports/TeamReports/AllGenZeroCoaches',
  [REPORT_NEW_GEN0_COACHES]: 'Reports/TeamReports/NewGenZeroCoaches',
  [REPORT_ALL_COACHES_IN_ORGANIZATION]:
    'Reports/TeamReports/AllOrganizationCoaches',
  [MY_TEAMS]: 'Reports/TeamReports/MyTeams',
  [REPORT_NEW_COACHES_IN_ORGANIZATION]:
    'Reports/TeamReports/NewOrganizationCoaches',
  [REPORT_EARNED_CAB]: 'Reports/TeamReports/EarnedCAB',

  // updated API Eligible CAB
  [REPORT_ELIGIBLE_ACCELERATOR_BONUS]: 'Reports/TeamReports/EligibleBonus',

  // updated API Eligible CSBP
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR]:
    'Reports/TeamReports/EligibleBonus',
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS]:
    'Reports/TeamReports/EligibleBonus',
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX]:
    'Reports/TeamReports/EligibleBonus',
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE]:
    'Reports/TeamReports/EligibleBonus',
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO]: 'Reports/TeamReports/EligibleBonus',

  // updated API Earned CSBP
  [REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS]: 'Reports/TeamReports/EarnedBonus',

  // updated API Earned CAB
  [REPORT_EARNED_ACCELERATOR_BONUS]: 'Reports/TeamReports/EarnedBonus',

  [REPORT_ELIGIBLE_FOR_INCENTIVE_TRIP]:
    'Reports/TeamReports/EligibleForIncentiveTrip',
  [REPORT_QUALIFIED_FOR_INCENTIVE_TRIP]:
    'Reports/TeamReports/QualifiedForIncentiveTrip',

  [REPORT_EARNED_QSB]: 'Reports/TeamReports/EarnedQSB',
  [REPORT_NOT_CERTIFIED]: 'Reports/TeamReports/NotCertified',
  [REPORT_EXPIRING_WELLNESS]:
    'Reports/ClientReports/OrganizationClientsWithExpiringWellnessCredits',
  [REPORT_EXPIRING_REWARDS]:
    'Reports/ClientReports/AllOrganizationExpiringRewards',

  [`${REPORT_ALL_FRONTLINE_CLIENTS}_RECORD`]:
    'Reports/ClientReports/AllFrontlineClients/Client',
  [`${REPORT_ALL_GEN0_CLIENTS}_RECORD`]:
    'Reports/ClientReports/AllGenZeroClients/Client',
  [`${REPORT_ALL_ORGANIZATION_CLIENTS}_RECORD`]:
    'Reports/ClientReports/AllOrganizationClients/Client',
  [`${REPORT_NEW_FRONTLINE_CLIENTS}_RECORD`]:
    'Reports/ClientReports/NewFrontlineClients/Client',
  [`${REPORT_NEW_GEN0_CLIENTS}_RECORD`]:
    'Reports/ClientReports/NewGenZeroClients/Client',
  [`${REPORT_NEW_ORGANIZATION_CLIENTS}_RECORD`]:
    'Reports/ClientReports/NewOrganizationClients/Client',
  [`${REPORT_ALL_FRONTLINE_ORDERS}_RECORD`]:
    'Reports/ClientReports/AllFrontlineOrders/Client',
  [`${REPORT_ALL_GEN0_ORDERS}_RECORD`]:
    'Reports/ClientReports/AllGenZeroOrders/Client',
  [`${REPORT_ALL_ORGANIZATION_ORDERS}_RECORD`]:
    'Reports/ClientReports/AllOrganizationOrders/Client',
  [`${REPORT_ALL_ORDERS}_RECORD`]: 'Orders/AllOrders',
  [`${REPORT_NEW_FRONTLINE_ORDERS}_RECORD`]:
    'Reports/ClientReports/NewFrontlineOrders/Client',
  [`${REPORT_NEW_GEN0_ORDERS}_RECORD`]:
    'Reports/ClientReports/NewGenZeroOrders/Client',
  [`${REPORT_NEW_ORGANIZATION_ORDERS}_RECORD`]:
    'Reports/ClientReports/NewOrganizationOrders/Client',
  [`${REPORT_PROJECTED_PREMIER_VOLUME}_RECORD`]:
    'Reports/ClientReports/ProjectedPremierVolume/Client',
  [`${REPORT_PREMIER_CANCELLATIONS}_RECORD`]:
    'Reports/ClientReports/PremierCancellations/Client',
  [`${REPORT_NEW_CLIENTS_WITHOUT_ORDERS}_RECORD`]:
    'Reports/ClientReports/NewClientsWithoutOrders/Client',
  [`${REPORT_RANK_UP}_RECORD`]: 'Reports/TeamReports/Promotions/Coach',
  [`${REPORT_ELIGIBLE_FOR_CAB}_RECORD`]:
    'Reports/TeamReports/EligibleForCAB/Coach',

  [`${REPORT_ELIGIBLE_FOR_INCENTIVE_TRIP}_RECORD`]:
    'Reports/TeamReports/EligibleForIncentiveTrip/Coach',
  [`${REPORT_QUALIFIED_FOR_INCENTIVE_TRIP}_RECORD`]:
    'Reports/TeamReports/QualifiedForIncentiveTrip/Coach',

  // updated API Earned Coach CAB
  [`${REPORT_EARNED_ACCELERATOR_BONUS}_RECORD`]:
    'Reports/TeamReports/EarnedBonus/Coach',

  // updated API Earned Coach CSBP
  [`${REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS}_RECORD`]:
    'Reports/TeamReports/EarnedBonus/Coach',

  // updated API Eligible Coach CAB
  [`${REPORT_ELIGIBLE_ACCELERATOR_BONUS}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',

  // updated API Eligible Coach CSBP

  [`${REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',
  [`${REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',
  [`${REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',
  [`${REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',
  [`${REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS}_RECORD`]:
    'Reports/TeamReports/EligibleBonus/Coach',

  [`${REPORT_ELIGIBLE_FOR_QSB}_RECORD`]:
    'Reports/TeamReports/EligibleForQSB/Coach',
  [`${REPORT_CLOSE_TO_EXECUTIVE_DIRECTOR_RANK}_RECORD`]:
    'Reports/TeamReports/CloseToED/Coach',
  [`${REPORT_CLOSE_TO_FIBC_RANK}_RECORD`]:
    'Reports/TeamReports/CloseToFIBC/Coach',
  [`${REPORT_CLOSE_TO_SENIOR_COACH_RANK}_RECORD`]:
    'Reports/TeamReports/CloseToSC/Coach',
  [`${REPORT_TEAMS_EXECUTIVE_DIRECTOR}_RECORD`]:
    'Reports/TeamReports/EDTeams/Coach',
  [`${REPORT_TEAMS_FIBC}_RECORD`]: 'Reports/TeamReports/FIBCTeams/Coach',
  [`${REPORT_TEAMS_FIBL}_RECORD`]: 'Reports/TeamReports/FIBLTeams/Coach',
  [`${REPORT_TEAMS_SENIOR_COACH}_RECORD`]: 'Reports/TeamReports/SCTeams/Coach',
  [`${REPORT_EXPIRING_COACH_RENEWAL}_RECORD`]:
    'Reports/TeamReports/ExpiringRenewals/Coach',
  [`${REPORT_COACHES_AT_RANK}_RECORD`]: 'Reports/TeamReports/AtRank/Coach',
  [`${REPORT_COACHES_BELOW_RANK}_RECORD`]:
    'Reports/TeamReports/BelowRank/Coach',
  [`${REPORT_COACHES_ABOVE_RANK}_RECORD`]:
    'Reports/TeamReports/AboveRank/Coach',
  [`${REPORT_FRONTLINE_COACHES}_RECORD`]:
    'Reports/TeamReports/AllFrontlineCoaches/Coach',
  [`${REPORT_ALL_FRONTLINE_COACHES}_RECORD`]:
    'Reports/TeamReports/AllFrontlineCoaches/Coach',
  [`${REPORT_NEW_FRONTLINE_COACHES}_RECORD`]:
    'Reports/TeamReports/NewFrontlineCoaches/Coach',
  [`${REPORT_ALL_GEN0_COACHES}_RECORD`]:
    'Reports/TeamReports/AllGenZeroCoaches/Coach',
  [`${REPORT_NEW_GEN0_COACHES}_RECORD`]:
    'Reports/TeamReports/NewGenZeroCoaches/Coach',
  [`${REPORT_ALL_COACHES_IN_ORGANIZATION}_RECORD`]:
    'Reports/TeamReports/AllOrganizationCoaches/Coach',
  [`${MY_TEAMS}_RECORD`]: 'Reports/TeamReports/MyTeams/Coach',
  [`${REPORT_NEW_COACHES_IN_ORGANIZATION}_RECORD`]:
    'Reports/TeamReports/NewOrganizationCoaches/Coach',
  [`${REPORT_EARNED_CAB}_RECORD`]: 'Reports/TeamReports/EarnedCAB/Coach',
  [`${REPORT_EARNED_QSB}_RECORD`]: 'Reports/TeamReports/EarnedQSB/Coach',
  [`${REPORT_NOT_CERTIFIED}_RECORD`]: 'Reports/TeamReports/NotCertified/Coach',
  [`${REPORT_INACTIVE_CLIENTS}_RECORD`]:
    'Reports/ClientReports/InactiveOrganizationClients/Client',
  [`${REPORT_EXPIRING_WELLNESS}_RECORD`]:
    'Reports/ClientReports/OrganizationClientsWithExpiringWellnessCredits/Client',
  [`${REPORT_EXPIRING_REWARDS}_RECORD`]:
    'Reports/ClientReports/AllOrganizationExpiringRewards/Client',

  [FETCH_CLIENTS_ALERTS]: 'Clients/Alerts',
  [DISMISS_CLIENTS_ALERTS]: 'Clients/Alerts',
  [FETCH_MULE_AUTH_TOKEN]: 'login',
  [FETCH_MULE_REVOKE_TOKEN]: 'revokeToken',
  [FETCH_SFMC_CONTACT_KEY]: 'user/crm/contactId',
  [CANDIDATES]: 'Candidates',
  [FETCH_MWL_HEALTH_ROOT_PROFILE]: '/coach/coach-sharing-profile',
  [FETCH_MSWL_HEALTH_ROOT_PROFILE]: '/coach/mswl',
  [FETCH_CLIENT_WEIGHT_GRAPH_DETAILS]: '/coach/weight-details',
  [FETCH_CLIENT_BODY_MEASUREMENTS]: '/coach/bodymeasurements',
  [FETCH_CLIENT_START_DATE]: 'user/journeyProfile/startDate',
  [FETCH_CLIENT_MEAL_PLAN]: 'user/journeyProfile/mealPlan',
  [FETCH_COACH_SHARE_PLAN]: 'coach',
  [FETCH_CLIENT_MY_WHY]: 'user/journeyProfile/myWhy',
  [POST_CANDIDATE_REMINDER]: 'Candidates/Reminder',
  [DELETE_CANDIDATE_REMINDER]: 'Candidates/Reminder',
  [UPDATE_CANDIDATE_REMINDER]: 'Candidates/Reminder',
  [FETCH_EDGE_365_CHALLENGE_TASKS]: 'Coaches/Edge365',
  [UPDATE_EDGE_365_CHALLENGE_TASKS]: 'Coaches/Edge365',

  [FETCH_BUSINESS_PROFILE]: 'Coaches/BusinessProfile',
  [PATCH_BUSINESS_PROFILE]: 'Coaches/BusinessProfile',
  [FETCH_SOCIAL_MEDIA]: 'Coaches/SocialNetworks',
  [PATCH_SOCIAL_MEDIA]: 'Coaches/SocialNetworks',
  [FETCH_LEADS_POOL]: 'Coaches/LeadsPool',
  [PATCH_LEADS_POOL]: 'Coaches/LeadsPool',
  [FETCH_HEALTH_CARE_DATA]: 'Coaches/HealthCareProvider',
  [PUT_HEALTH_CARE_DATA]: 'Coaches/HealthCareProvider',
  [FETCH_SUPPORTED_LANGUAGES]: 'Coaches/SupportedLanguages',
  [PUT_SUPPORTED_LANGUAGES]: 'Coaches/SupportedLanguages',
  [POST_COACH_IMAGE]: 'Coaches/Image',
};

const mockApiArray: any = [];

export const request = async (
  requestName: keyof typeof REQUEST_ROUTE,
  options: any = {},
  isMock: boolean = false,
) => {
  const {skipTestingOptions, isRawBody = false, ...restOptions} = options || {};
  const endPoint = REQUEST_ROUTE[requestName];
  // const body = {...(restOptions.body || {})};
  const body = isRawBody ? restOptions.body : {...(restOptions.body || {})};
  const getHeaders = restOptions.getHeaders || [];

  if (ENABLE_TESTING_OPTIONS && !skipTestingOptions && !isRawBody) {
    const [
      currentPeriodId,
      lastPeriodId,
      latestAcceptedPeriodId,
      openPeriodId,
      presentDate,
    ] = await Promise.all([
      getTestingCurrentPeriodId(),
      getTestingLastPeriodId(),
      getTestingLatestAcceptedPeriodId(),
      getTestingOpenPeriodId(),
      getTestingDate(),
    ]);

    if (
      currentPeriodId &&
      lastPeriodId &&
      latestAcceptedPeriodId &&
      openPeriodId &&
      presentDate
    ) {
      body.currentPeriodId = currentPeriodId;
      body.lastPeriodId = lastPeriodId;
      body.latestAcceptedPeriodId = latestAcceptedPeriodId;
      body.openPeriodId = openPeriodId;
      body.presentDate = presentDate;
    }
  }

  const isMockApi =
    find(mockApiArray, (val: any) => val === requestName) || isMock;

  const {error, data, meta, responseHeaders} = await fetchJSON(
    endPoint,
    {
      ...restOptions,
      body,
      isRawBody,
    },
    getHeaders,
    isMockApi,
  );
  const {status, serverError} = meta;

  if (serverError) {
    getErrorTrackingUtilDefaultInstance().trackApiError({
      status,
      endPoint,
      body: error,
    });
  }

  // map API status code to appropriate app exceptions
  const errorMap = {
    504: SERVER_IS_DOWN_EXCEPTION,
    500: SERVER_IS_DOWN_EXCEPTION,
    505: FAILED_TO_PROCESS_REQUEST_EXCEPTION,
    403: NOT_AUTHORIZED_EXCEPTION,
    401: NOT_AUTHORIZED_EXCEPTION,
    400: BAD_REQUEST,
    0: NO_NETWORK_EXCEPTION,
  };

  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  if (errorMap[status] || error) {
    const errorObj = {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      code: errorMap[status] || UNKNOWN_EXCEPTION,
      message: error,
      meta,
    };
    throw errorObj;
  }

  if (isEmpty(responseHeaders)) {
    return data;
  } else {
    return {data, responseHeaders};
  }
};
